import { ref, computed } from "vue"
import { api } from "@/lib/api"
import type { Ref } from "vue"
import { debug, error as logError } from "@/utils/debug"

export interface FollowStatus {
  is_following: boolean
  follows_you: boolean
  follow_status: "none" | "pending" | "accepted"
  is_blocked: boolean
  blocked_you: boolean
  can_follow: boolean
}

interface FollowRequest {
  id: string
  follower_id: string
  requested_at: string
  follower_username: string
  follower_profile_pic_url: string | null
  follower_bio: string | null
}

export interface Follower {
  id: string
  follower_id: string
  role: string | null
  accepted_at: string
  follower_username: string
  follower_profile_pic_url: string | null
  follower_bio: string | null
  is_mutual: boolean
}

export interface Following {
  id: string
  following_id: string
  role: string | null
  accepted_at: string
  following_username: string
  following_profile_pic_url: string | null
  following_bio: string | null
  is_mutual: boolean
}

export interface MutualFollow {
  id: string
  user_id: string
  username: string
  profile_pic_url: string | null
  bio: string | null
  your_role: string | null
  their_role: string | null
  accepted_at: string
}

export interface UserSearchResult {
  id: string
  username: string
  bio: string | null
  profile_pic_url: string | null
  is_public: boolean
  followers_count: number
  following_count: number
  is_following: boolean
  follows_you: boolean
  is_blocked: boolean
  blocked_you: boolean
}

// Cache entry with timestamp
interface CacheEntry<T> {
  data: T
  timestamp: number
}

// Shared state (singleton pattern)
const followStatuses = ref<Map<string, FollowStatus>>(new Map())
const followRequests = ref<FollowRequest[]>([])
const isLoadingRequests = ref(false)
const isLoadingStatus = ref(false)

// Cache for followers/following data
const followersCache = ref<Map<string, CacheEntry<Follower[]>>>(new Map())
const followingCache = ref<Map<string, CacheEntry<Following[]>>>(new Map())
const mutualFollowsCache = ref<Map<string, CacheEntry<MutualFollow[]>>>(new Map())

// Cache expiration time (5 minutes)
const CACHE_EXPIRATION_MS = 5 * 60 * 1000

const pendingRequestsCount = computed(() => followRequests.value.length)

/**
 * Check if cache entry is still valid
 */
function isCacheValid<T>(entry: CacheEntry<T> | undefined): boolean {
  if (!entry) return false
  const now = Date.now()
  return now - entry.timestamp < CACHE_EXPIRATION_MS
}

/**
 * Get cache key for user-specific data
 */
function getCacheKey(userId?: string): string {
  return userId || "current-user"
}

/**
 * Invalidate follower/following cache for a specific user
 * Call this after follow/unfollow/accept/reject actions
 */
function invalidateFollowCache(userId?: string) {
  const cacheKey = getCacheKey(userId)
  followersCache.value.delete(cacheKey)
  followingCache.value.delete(cacheKey)
  mutualFollowsCache.value.delete(cacheKey)
  debug(`🗑️ Invalidated follow cache for ${cacheKey}`)
}

/**
 * Invalidate all follow caches
 * Call this when current user performs a follow action that affects multiple users
 */
function invalidateAllFollowCaches() {
  followersCache.value.clear()
  followingCache.value.clear()
  mutualFollowsCache.value.clear()
  debug(`🗑️ Invalidated all follow caches`)
}

export function useFollowing() {
  /**
   * Check follow status between current user and target user
   */
  async function checkFollowStatus(userId: string): Promise<FollowStatus | null> {
    if (isLoadingStatus.value) return null

    isLoadingStatus.value = true
    try {
      const { data, error } = await api<FollowStatus>("checkFollowStatusUser", {
        user_id: userId,
      })

      if (error) {
        logError("Failed to check follow status:", error)
        return null
      }

      if (data) {
        followStatuses.value.set(userId, data)
      }

      return data
    } finally {
      isLoadingStatus.value = false
    }
  }

  /**
   * Send a follow request or follow a public user
   */
  async function sendFollowRequest(userId: string): Promise<boolean> {
    try {
      const { data, error } = await api<{
        status: "pending" | "accepted"
        message: string
      }>("sendFollowRequestUser", {
        target_user_id: userId,
      })

      if (error) {
        logError("Failed to send follow request:", error)
        return false
      }

      // Invalidate cache for both current user and target user
      invalidateAllFollowCaches()

      // Refresh status
      await checkFollowStatus(userId)

      return true
    } catch (err) {
      logError("Error sending follow request:", err)
      return false
    }
  }

  /**
   * Cancel a pending follow request
   */
  async function cancelFollowRequest(userId: string): Promise<boolean> {
    try {
      const { data, error } = await api("cancelFollowRequestUser", {
        user_id: userId,
      })

      if (error) {
        logError("Failed to cancel follow request:", error)
        return false
      }

      // Invalidate cache
      invalidateAllFollowCaches()

      // Refresh status
      await checkFollowStatus(userId)

      return true
    } catch (err) {
      logError("Error cancelling follow request:", err)
      return false
    }
  }

  /**
   * Unfollow a user
   */
  async function unfollowUser(userId: string): Promise<boolean> {
    try {
      const { data, error } = await api("unfollowUser", {
        user_id: userId,
      })

      if (error) {
        logError("Failed to unfollow user:", error)
        return false
      }

      // Invalidate cache
      invalidateAllFollowCaches()

      // Refresh status
      await checkFollowStatus(userId)

      return true
    } catch (err) {
      logError("Error unfollowing user:", err)
      return false
    }
  }

  /**
   * Fetch pending follow requests
   */
  async function fetchFollowRequests(): Promise<void> {
    if (isLoadingRequests.value) return

    isLoadingRequests.value = true
    try {
      const { data, error } = await api<FollowRequest[]>("fetchFollowRequestsUser", {
        limit: 50,
      })

      if (error) {
        logError("Failed to fetch follow requests:", error)
        return
      }

      followRequests.value = data || []
    } finally {
      isLoadingRequests.value = false
    }
  }

  /**
   * Respond to a follow request (accept or reject)
   */
  async function respondToFollowRequest(
    followerId: string,
    action: "accept" | "reject",
  ): Promise<boolean> {
    try {
      const { data, error } = await api("respondToFollowRequestUser", {
        follower_id: followerId,
        action,
      })

      if (error) {
        logError("Failed to respond to follow request:", error)
        return false
      }

      // Invalidate cache since follower list changed
      invalidateAllFollowCaches()

      // Remove from local list
      followRequests.value = followRequests.value.filter((req) => req.follower_id !== followerId)

      return true
    } catch (err) {
      logError("Error responding to follow request:", err)
      return false
    }
  }

  /**
   * Block a user
   */
  async function blockUser(userId: string, reason?: string): Promise<boolean> {
    try {
      const { data, error } = await api("blockUser", {
        user_id: userId,
        reason,
      })

      if (error) {
        logError("Failed to block user:", error)
        return false
      }

      // Invalidate cache since blocking affects follow relationships
      invalidateAllFollowCaches()

      // Refresh status
      await checkFollowStatus(userId)

      return true
    } catch (err) {
      logError("Error blocking user:", err)
      return false
    }
  }

  /**
   * Unblock a user
   */
  async function unblockUser(userId: string): Promise<boolean> {
    try {
      const { data, error } = await api("unblockUser", {
        user_id: userId,
      })

      if (error) {
        logError("Failed to unblock user:", error)
        return false
      }

      // Invalidate cache
      invalidateAllFollowCaches()

      // Refresh status
      await checkFollowStatus(userId)

      return true
    } catch (err) {
      logError("Error unblocking user:", err)
      return false
    }
  }

  /**
   * Report a user
   */
  async function reportUser(
    userId: string,
    reportType: "spam" | "harassment" | "inappropriate_content" | "fake_account" | "other",
    description: string,
  ): Promise<boolean> {
    try {
      const { data, error } = await api<{
        success: boolean
        auto_blocked: boolean
        email_sent: boolean
      }>("reportUser", {
        reported_user_id: userId,
        report_type: reportType,
        description,
      })

      if (error) {
        logError("Failed to report user:", error)
        return false
      }

      // Refresh status (user should now be blocked)
      await checkFollowStatus(userId)

      return true
    } catch (err) {
      logError("Error reporting user:", err)
      return false
    }
  }

  /**
   * Get cached follow status for a user
   */
  function getFollowStatus(userId: string): FollowStatus | undefined {
    return followStatuses.value.get(userId)
  }

  /**
   * Fetch followers for a user
   */
  async function fetchFollowers(
    userId?: string,
    limit = 50,
    offset = 0,
    force = false,
  ): Promise<Follower[]> {
    const cacheKey = getCacheKey(userId)

    // Return cached data if valid and not forcing refresh
    if (!force && offset === 0) {
      const cached = followersCache.value.get(cacheKey)
      if (cached && isCacheValid(cached)) {
        debug(`📦 Using cached followers for ${cacheKey}`)
        return cached.data
      }
    }

    try {
      debug(`🌐 Fetching followers from API for ${cacheKey}`)
      const { data, error } = await api<Follower[]>("fetchFollowersUser", {
        user_id: userId,
        limit,
        offset,
      })

      if (error) {
        logError("Failed to fetch followers:", error)
        return []
      }

      // Cache the result (only for first page)
      if (offset === 0 && data) {
        followersCache.value.set(cacheKey, {
          data: data,
          timestamp: Date.now(),
        })
      }

      return data || []
    } catch (err) {
      logError("Error fetching followers:", err)
      return []
    }
  }

  /**
   * Fetch following for a user
   */
  async function fetchFollowing(
    userId?: string,
    limit = 50,
    offset = 0,
    force = false,
  ): Promise<Following[]> {
    const cacheKey = getCacheKey(userId)

    // Return cached data if valid and not forcing refresh
    if (!force && offset === 0) {
      const cached = followingCache.value.get(cacheKey)
      if (cached && isCacheValid(cached)) {
        debug(`📦 Using cached following for ${cacheKey}`)
        return cached.data
      }
    }

    try {
      debug(`🌐 Fetching following from API for ${cacheKey}`)
      const { data, error } = await api<Following[]>("fetchFollowingUser", {
        user_id: userId,
        limit,
        offset,
      })

      if (error) {
        logError("Failed to fetch following:", error)
        return []
      }

      // Cache the result (only for first page)
      if (offset === 0 && data) {
        followingCache.value.set(cacheKey, {
          data: data,
          timestamp: Date.now(),
        })
      }

      return data || []
    } catch (err) {
      logError("Error fetching following:", err)
      return []
    }
  }

  /**
   * Fetch mutual follows for a user
   */
  async function fetchMutualFollows(
    userId?: string,
    limit = 50,
    offset = 0,
    force = false,
  ): Promise<MutualFollow[]> {
    const cacheKey = getCacheKey(userId)

    // Return cached data if valid and not forcing refresh
    if (!force && offset === 0) {
      const cached = mutualFollowsCache.value.get(cacheKey)
      if (cached && isCacheValid(cached)) {
        debug(`📦 Using cached mutual follows for ${cacheKey}`)
        return cached.data
      }
    }

    try {
      debug(`🌐 Fetching mutual follows from API for ${cacheKey}`)
      const { data, error } = await api<MutualFollow[]>("fetchMutualFollowsUser", {
        user_id: userId,
        limit,
        offset,
      })

      if (error) {
        logError("Failed to fetch mutual follows:", error)
        return []
      }

      // Cache the result (only for first page)
      if (offset === 0 && data) {
        mutualFollowsCache.value.set(cacheKey, {
          data: data,
          timestamp: Date.now(),
        })
      }

      return data || []
    } catch (err) {
      logError("Error fetching mutual follows:", err)
      return []
    }
  }

  /**
   * Update the role assigned to a follower
   */
  async function updateFollowerRole(followerId: string, role: string | null): Promise<boolean> {
    try {
      const { data, error } = await api("updateFollowerRoleUser", {
        follower_id: followerId,
        role,
      })

      if (error) {
        logError("Failed to update follower role:", error)
        return false
      }

      return true
    } catch (err) {
      logError("Error updating follower role:", err)
      return false
    }
  }

  /**
   * Search for users by username
   */
  async function searchUsers(
    query: string,
    limit = 20,
    offset = 0,
    includePrivate = true,
  ): Promise<UserSearchResult[]> {
    if (!query.trim()) {
      return []
    }

    try {
      const { data, error } = await api<UserSearchResult[]>("searchUsersUser", {
        query: query.trim(),
        limit,
        offset,
        include_private: includePrivate,
      })

      if (error) {
        logError("Failed to search users:", error)
        return []
      }

      return data || []
    } catch (err) {
      logError("Error searching users:", err)
      return []
    }
  }

  return {
    // State
    followStatuses,
    followRequests,
    isLoadingRequests,
    isLoadingStatus,
    pendingRequestsCount,

    // Methods
    checkFollowStatus,
    sendFollowRequest,
    cancelFollowRequest,
    unfollowUser,
    fetchFollowRequests,
    respondToFollowRequest,
    blockUser,
    unblockUser,
    reportUser,
    getFollowStatus,
    fetchFollowers,
    fetchFollowing,
    fetchMutualFollows,
    updateFollowerRole,
    searchUsers,

    // Cache management
    invalidateFollowCache,
    invalidateAllFollowCaches,
  }
}
